import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, Download, Users, Loader2 } from "lucide-react";
import html2canvas from "html2canvas";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { useFb } from "../utils/football";

const COLORS = ["#f59e0b", "#10b981", "#3b82f6", "#8b5cf6", "#ec4899", "#ef4444", "#14b8a6", "#f97316", "#a855f7", "#64748b"];
const SCOPES = [["all", "All"], ["cricket", "Cricket"], ["football", "Football"], ["overlap", "Overlap"]];

const toEntries = (obj = {}) => Object.entries(obj).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

const topN = (entries, n) => {
  if (entries.length <= n) return entries;
  const top = entries.slice(0, n);
  const other = entries.slice(n).reduce((a, e) => a + e.value, 0);
  return other > 0 ? [...top, { name: "Other", value: other }] : top;
};

const ChartCard = ({ title, children, testid }) => (
  <div className="rounded-xl border border-slate-700 bg-slate-800 p-4" data-testid={testid}>
    <h3 className="mb-3 text-sm font-bold text-white">{title}</h3>
    {children}
  </div>
);

const Tile = ({ label, value, testid }) => (
  <div className="rounded-xl border border-slate-700 bg-slate-800 p-3 text-center" data-testid={testid}>
    <div className="text-2xl font-extrabold text-amber-400">{value}</div>
    <div className="mt-0.5 text-[11px] text-slate-400">{label}</div>
  </div>
);

export default function AdminAudiencePage() {
  const fb = useFb();
  const navigate = useNavigate();
  const [scope, setScope] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const captureRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await fb.get("/football/admin/audience", { scope })); } catch { setData(null); }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);

  useEffect(() => { load(); }, [load]);

  const exportImage = async () => {
    if (!captureRef.current) return;
    setExporting(true);
    try {
      const canvas = await html2canvas(captureRef.current, { backgroundColor: "#0f172a", scale: 2 });
      const link = document.createElement("a");
      link.download = `apl-audience-${scope}-${new Date().toISOString().slice(0, 10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } catch (e) { /* ignore */ } finally { setExporting(false); }
  };

  const country = data ? topN(toEntries(data.by_country), 10) : [];
  const city = data ? toEntries(data.by_city).slice(0, 15) : [];
  const age = data ? ["<18", "18-24", "25-34", "35-44", "45-54", "55+", "unknown"].map((b) => ({ name: b, value: data.by_age_bucket?.[b] || 0 })).filter((e) => e.value > 0) : [];
  const gender = data ? toEntries(data.by_gender) : [];
  const eng = data?.engagement || {};

  return (
    <div className="min-h-screen bg-slate-900 pb-12" data-testid="admin-audience-page">
      <div className="border-b border-slate-700 bg-slate-800 px-4 py-4">
        <button onClick={() => navigate("/admin")} className="mb-2 inline-flex items-center gap-1 text-sm text-slate-400" data-testid="audience-back"><ChevronLeft size={18} /> Admin</button>
        <div className="flex items-center justify-between">
          <h1 className="flex items-center gap-2 text-xl font-extrabold text-white"><Users size={22} className="text-amber-400" /> Audience Dashboard</h1>
          <button onClick={exportImage} disabled={exporting || loading} className="flex items-center gap-1.5 rounded-lg bg-amber-500 px-3 py-2 text-sm font-bold text-slate-900 disabled:opacity-50" data-testid="audience-export-btn">
            {exporting ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Export
          </button>
        </div>
        {data && <p className="mt-2 text-sm text-slate-400" data-testid="audience-total">Total platform users: <strong className="text-white">{data.total_platform_users}</strong></p>}
      </div>

      <div className="mx-auto max-w-3xl px-4">
        <div className="mt-4 flex gap-2 rounded-xl border border-slate-700 bg-slate-800 p-1">
          {SCOPES.map(([key, label]) => (
            <button key={key} onClick={() => setScope(key)} className={`flex-1 rounded-lg py-2 text-sm font-bold ${scope === key ? "bg-amber-500 text-slate-900" : "text-slate-400"}`} data-testid={`audience-tab-${key}`}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div className="mt-6 h-80 animate-pulse rounded-xl bg-slate-800" />
        ) : !data ? (
          <div className="mt-10 text-center text-sm text-slate-500">Failed to load audience data.</div>
        ) : (
          <div ref={captureRef} className="mt-4 space-y-4 bg-slate-900 p-1">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="audience-engagement">
              <Tile label="Total Registered" value={eng.total_registered ?? 0} testid="tile-registered" />
              <Tile label="Active (7d)" value={eng.active_7d ?? 0} testid="tile-active7d" />
              <Tile label="Active Today" value={eng.active_today ?? 0} testid="tile-activetoday" />
              <Tile label="Avg Session (min)" value={eng.avg_session_minutes ?? 0} testid="tile-session" />
            </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <ChartCard title="Country (Top 10)" testid="chart-country">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={country} dataKey="value" nameKey="name" outerRadius={80} label>
                      {country.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>

              <ChartCard title="Gender" testid="chart-gender">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={gender} dataKey="value" nameKey="name" outerRadius={80} label>
                      {gender.map((e, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>

            <ChartCard title="City (Top 15)" testid="chart-city">
              <ResponsiveContainer width="100%" height={Math.max(200, city.length * 28)}>
                <BarChart data={city} layout="vertical" margin={{ left: 20 }}>
                  <XAxis type="number" stroke="#94a3b8" fontSize={11} />
                  <YAxis type="category" dataKey="name" stroke="#94a3b8" fontSize={11} width={90} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#10b981" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <ChartCard title="Age Range" testid="chart-age">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={age}>
                  <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="value" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>
        )}
      </div>
    </div>
  );
}
